import React, { useState, useRef, useCallback } from 'react';
import { useLanguage } from '@/LanguageContext';
import { motion } from 'framer-motion';
import { Send, Loader2, MapPin, Clock, Mail, Phone, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* --- Layer 1: Checkbox "No soy robot" with 2s delay --- */
const CheckboxCaptcha = ({ verified, onVerify, lang }) => {
  const [checking, setChecking] = useState(false);

  const labels = {
    en: { label: "I'm not a robot", verifying: 'Verifying...', verified: 'Verified' },
    es: { label: 'No soy un robot', verifying: 'Verificando...', verified: 'Verificado' },
  };
  const l = labels[lang] || labels.en;

  const handleClick = () => {
    if (verified || checking) return;
    setChecking(true);
    setTimeout(() => {
      setChecking(false);
      onVerify();
    }, 2000);
  };

  return (
    <div className="mb-4" data-testid="checkbox-captcha-wrapper">
      <div
        onClick={handleClick}
        className={`flex items-center gap-4 p-4 border-2 cursor-pointer select-none transition-all duration-300 ${
          verified
            ? 'border-emerald-400 bg-emerald-50'
            : checking
            ? 'border-[#C5A55A] bg-[#FCFBF7]'
            : 'border-slate-200 bg-white hover:border-slate-300'
        }`}
        data-testid="checkbox-captcha"
      >
        <div className={`w-7 h-7 flex-shrink-0 border-2 flex items-center justify-center transition-all duration-300 ${
          verified ? 'border-emerald-500 bg-emerald-500' : checking ? 'border-[#C5A55A]' : 'border-slate-300'
        }`}>
          {verified && (
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 7.5L5.5 11L12 3" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
          {checking && (
            <div className="w-4 h-4 border-2 border-[#C5A55A] border-t-transparent rounded-full animate-spin" />
          )}
        </div>
        <span className={`text-sm font-medium ${verified ? 'text-emerald-700' : 'text-[#0A1628]'}`}>
          {verified ? l.verified : checking ? l.verifying : l.label}
        </span>
        <div className="ml-auto flex flex-col items-end">
          <ShieldCheck className={`w-5 h-5 ${verified ? 'text-emerald-500' : 'text-slate-300'}`} />
          <span className="text-[9px] text-slate-400 mt-0.5">MedecineSupply</span>
        </div>
      </div>
    </div>
  );
};

/* --- Layer 2: Slider verification --- */
const SliderCaptcha = ({ verified, onVerify, lang }) => {
  const trackRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [progress, setProgress] = useState(0);

  const labels = {
    en: { slide: 'Slide to confirm', verified: 'Confirmed' },
    es: { slide: 'Desliza para confirmar', verified: 'Confirmado' },
  };
  const l = labels[lang] || labels.en;

  const updateProgress = useCallback((clientX) => {
    if (!trackRef.current) return;
    const rect = trackRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const pct = Math.round((x / rect.width) * 100);
    setProgress(pct);
    if (pct >= 96) { setProgress(100); onVerify(); }
  }, [onVerify]);

  const handleStart = (clientX) => { if (!verified) { setDragging(true); updateProgress(clientX); } };
  const handleMove = (clientX) => { if (dragging && !verified) updateProgress(clientX); };
  const handleEnd = () => { if (!verified && progress < 96) setProgress(0); setDragging(false); };

  return (
    <div
      className="mb-6"
      onMouseMove={(e) => handleMove(e.clientX)}
      onMouseUp={handleEnd}
      onMouseLeave={handleEnd}
      onTouchMove={(e) => handleMove(e.touches[0].clientX)}
      onTouchEnd={handleEnd}
      data-testid="slider-captcha-wrapper"
    >
      <div
        ref={trackRef}
        className={`relative h-12 overflow-hidden select-none ${
          verified ? 'bg-emerald-50 border-2 border-emerald-400' : 'bg-slate-100 border-2 border-slate-200'
        }`}
        data-testid="slider-captcha-track"
      >
        <div
          className={`absolute inset-y-0 left-0 transition-all ${dragging ? 'duration-0' : 'duration-300'} ${
            verified ? 'bg-emerald-400' : 'bg-[#C5A55A]/30'
          }`}
          style={{ width: `${progress}%` }}
        />
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          {verified ? (
            <span className="text-emerald-700 font-semibold text-sm tracking-wider flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" /> {l.verified}
            </span>
          ) : (
            <span className="text-slate-400 text-sm tracking-wide">{l.slide}</span>
          )}
        </div>
        {!verified && (
          <div
            className="absolute top-0 bottom-0 w-12 bg-[#C5A55A] flex items-center justify-center cursor-grab active:cursor-grabbing shadow-md"
            style={{ left: `calc(${progress}% - ${progress > 5 ? 24 : 0}px)` }}
            onMouseDown={(e) => handleStart(e.clientX)}
            onTouchStart={(e) => handleStart(e.touches[0].clientX)}
            data-testid="slider-captcha-thumb"
          >
            <svg width="18" height="14" fill="none" viewBox="0 0 18 14">
              <path d="M11 1l6 6-6 6M1 7h16" stroke="#0A1628" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        )}
      </div>
    </div>
  );
};

const Contact = () => {
  const { t, lang } = useLanguage();
  const [activeTab, setActiveTab] = useState('contact');
  const [loading, setLoading] = useState(false);

  // Contact form: checkbox + slider states
  const [contactCheckbox, setContactCheckbox] = useState(false);
  const [contactSlider, setContactSlider] = useState(false);
  // Quote form: checkbox + slider states
  const [quoteCheckbox, setQuoteCheckbox] = useState(false);
  const [quoteSlider, setQuoteSlider] = useState(false);

  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', company: '', message: '', website_url: '' });
  const [quoteForm, setQuoteForm] = useState({ name: '', email: '', phone: '', company: '', products: '', quantity: '', message: '', website_url: '' });

  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: (i) => ({
      opacity: 1, y: 0,
      transition: { duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
    }),
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      toast.error(lang === 'es' ? 'Complete todos los campos requeridos.' : 'Please fill in all required fields.');
      return;
    }
    if (!contactCheckbox || !contactSlider) {
      toast.error(lang === 'es' ? 'Complete ambas verificaciones de seguridad.' : 'Please complete both security verifications.');
      return;
    }
    setLoading(true);
    try {
      const payload = { ...contactForm, form_type: 'contact', captcha_verified: true };
      await axios.post(`${API}/contact`, payload);
      toast.success(lang === 'es' ? 'Mensaje enviado! Le responderemos en 24 horas.' : 'Message sent successfully! We will get back to you within 24 hours.');
      setContactForm({ name: '', email: '', phone: '', company: '', message: '', website_url: '' });
      setContactCheckbox(false);
      setContactSlider(false);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(msg || (lang === 'es' ? 'Error al enviar. Intente de nuevo.' : 'Failed to send message. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    if (!quoteForm.name || !quoteForm.email) {
      toast.error(lang === 'es' ? 'Complete todos los campos requeridos.' : 'Please fill in all required fields.');
      return;
    }
    if (!quoteCheckbox || !quoteSlider) {
      toast.error(lang === 'es' ? 'Complete ambas verificaciones de seguridad.' : 'Please complete both security verifications.');
      return;
    }
    setLoading(true);
    try {
      const payload = { ...quoteForm, captcha_verified: true };
      await axios.post(`${API}/quote`, payload);
      toast.success(lang === 'es' ? 'Cotizacion enviada! Responderemos en 24 horas.' : 'Quote request submitted! Our team will respond within 24 hours.');
      setQuoteForm({ name: '', email: '', phone: '', company: '', products: '', quantity: '', message: '', website_url: '' });
      setQuoteCheckbox(false);
      setQuoteSlider(false);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(msg || (lang === 'es' ? 'Error al enviar. Intente de nuevo.' : 'Failed to submit quote request. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full bg-slate-50 border border-slate-200 focus:border-[#0A1628] focus:ring-1 focus:ring-[#0A1628] h-12 px-4 text-[#0A1628] placeholder:text-slate-400 outline-none transition-all duration-300 text-sm";
  const textareaClass = "w-full bg-slate-50 border border-slate-200 focus:border-[#0A1628] focus:ring-1 focus:ring-[#0A1628] px-4 py-3 text-[#0A1628] placeholder:text-slate-400 outline-none transition-all duration-300 text-sm min-h-[120px] resize-none";

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-[#F4F7FB]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <motion.p
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0}
            className="text-[#C5A55A] text-sm font-mono tracking-[0.3em] uppercase mb-4"
          >
            {t('contact.overline')}
          </motion.p>
          <motion.h2
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1}
            className="font-heading text-4xl md:text-5xl font-semibold tracking-tight text-[#0A1628] mb-4"
            data-testid="contact-title"
          >
            {t('contact.title')}
          </motion.h2>
          <motion.p
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={2}
            className="text-slate-600 text-base md:text-lg leading-relaxed max-w-2xl mx-auto"
          >
            {t('contact.subtitle')}
          </motion.p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Info Sidebar */}
          <motion.div
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={3}
            className="lg:col-span-1 space-y-6"
          >
            <div className="bg-[#0A1628] p-8" data-testid="contact-info-office">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-10 h-10 flex items-center justify-center border border-[#C5A55A]/30 flex-shrink-0">
                  <MapPin className="w-5 h-5 text-[#C5A55A]" />
                </div>
                <div>
                  <h4 className="text-white font-semibold text-sm tracking-wider uppercase mb-2">{t('contact.office')}</h4>
                  <p className="text-slate-300 text-sm leading-relaxed">{t('contact.address')}</p>
                  <p className="text-slate-300 text-sm leading-relaxed">{t('contact.addressLine2')}</p>
                </div>
              </div>

              <div className="flex items-start gap-4 mb-6">
                <div className="w-10 h-10 flex items-center justify-center border border-[#C5A55A]/30 flex-shrink-0">
                  <Clock className="w-5 h-5 text-[#C5A55A]" />
                </div>
                <div>
                  <h4 className="text-white font-semibold text-sm tracking-wider uppercase mb-2">{t('contact.availability')}</h4>
                  <p className="text-slate-300 text-sm">{t('contact.hours.weekdays')}</p>
                  <p className="text-slate-300 text-sm">{t('contact.hours.saturday')}</p>
                </div>
              </div>

              <div className="flex items-start gap-4 mb-6">
                <div className="w-10 h-10 flex items-center justify-center border border-[#C5A55A]/30 flex-shrink-0">
                  <Mail className="w-5 h-5 text-[#C5A55A]" />
                </div>
                <div>
                  <h4 className="text-white font-semibold text-sm tracking-wider uppercase mb-2">{t('contact.emailLabel')}</h4>
                  <a href="mailto:info@medecinesupply.com" className="text-[#C5A55A] text-sm hover:text-[#D7C085] transition-colors">
                    {t('contact.email')}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center border border-[#C5A55A]/30 flex-shrink-0">
                  <Phone className="w-5 h-5 text-[#C5A55A]" />
                </div>
                <div>
                  <h4 className="text-white font-semibold text-sm tracking-wider uppercase mb-2">{t('contact.phoneLabel')}</h4>
                  <p className="text-slate-300 text-sm">{t('contact.phone')}</p>
                </div>
              </div>
            </div>

            <div className="relative overflow-hidden h-48">
              <iframe
                title="Medecine Supply Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3923.5!2d-66.855!3d10.493!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8c2a58f5c7c5c5c5%3A0x0!2sAv.+Francisco+de+Miranda%2C+Los+Palos+Grandes%2C+Chacao%2C+Caracas!5e0!3m2!1ses!2sve!4v1700000000000!5m2!1ses!2sve"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="google-maps-embed"
                className="absolute inset-0"
              />
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={4}
            className="lg:col-span-2 bg-white border border-slate-100 shadow-sm"
          >
            <div className="flex border-b border-slate-100">
              <button
                onClick={() => setActiveTab('contact')}
                className={`flex-1 py-4 text-sm font-semibold tracking-wider uppercase transition-all duration-300 ${
                  activeTab === 'contact'
                    ? 'text-[#0A1628] border-b-2 border-[#C5A55A] bg-[#FCFBF7]'
                    : 'text-slate-400 hover:text-slate-600'
                }`}
                data-testid="contact-tab-contact"
              >
                {t('contact.form.tabContact')}
              </button>
              <button
                onClick={() => setActiveTab('quote')}
                className={`flex-1 py-4 text-sm font-semibold tracking-wider uppercase transition-all duration-300 ${
                  activeTab === 'quote'
                    ? 'text-[#0A1628] border-b-2 border-[#C5A55A] bg-[#FCFBF7]'
                    : 'text-slate-400 hover:text-slate-600'
                }`}
                data-testid="contact-tab-quote"
              >
                {t('contact.form.tabQuote')}
              </button>
            </div>

            <div className="p-8 md:p-12">
              {activeTab === 'contact' ? (
                <form onSubmit={handleContactSubmit} data-testid="contact-form">
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-[#0A1628] mb-2">{t('contact.form.name')} *</label>
                      <input
                        type="text" className={inputClass} value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        data-testid="contact-name-input" required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#0A1628] mb-2">{t('contact.form.email')} *</label>
                      <input
                        type="email" className={inputClass} value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        data-testid="contact-email-input" required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#0A1628] mb-2">{t('contact.form.phone')}</label>
                      <input
                        type="tel" className={inputClass} value={contactForm.phone}
                        onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                        data-testid="contact-phone-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#0A1628] mb-2">{t('contact.form.company')}</label>
                      <input
                        type="text" className={inputClass} value={contactForm.company}
                        onChange={(e) => setContactForm({ ...contactForm, company: e.target.value })}
                        data-testid="contact-company-input"
                      />
                    </div>
                  </div>
                  <div className="mb-8">
                    <label className="block text-sm font-medium text-[#0A1628] mb-2">{t('contact.form.message')} *</label>
                    <textarea
                      className={textareaClass} value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      data-testid="contact-message-input" required
                    />
                  </div>
                  {/* Layer 3: Honeypot - invisible to humans */}
                  <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
                    <label htmlFor="website_url_c">Website</label>
                    <input
                      type="text" id="website_url_c" name="website_url" tabIndex="-1" autoComplete="off"
                      value={contactForm.website_url}
                      onChange={(e) => setContactForm({ ...contactForm, website_url: e.target.value })}
                      data-testid="contact-honeypot"
                    />
                  </div>
                  {/* Layer 1: Checkbox "No soy robot" */}
                  <CheckboxCaptcha verified={contactCheckbox} onVerify={() => setContactCheckbox(true)} lang={lang} />
                  {/* Layer 2: Slider */}
                  <SliderCaptcha verified={contactSlider} onVerify={() => setContactSlider(true)} lang={lang} />
                  <button
                    type="submit" disabled={loading}
                    className="w-full bg-[#C5A55A] text-[#0A1628] font-semibold px-8 py-4 uppercase tracking-wider transition-all duration-300 shadow-[0_4px_14px_0_rgba(197,165,90,0.39)] hover:shadow-[0_6px_20px_rgba(197,165,90,0.23)] hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                    data-testid="contact-submit-btn"
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                    {loading ? t('contact.form.sending') : t('contact.form.submitContact')}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleQuoteSubmit} data-testid="quote-form">
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-[#0A1628] mb-2">{t('contact.form.name')} *</label>
                      <input
                        type="text" className={inputClass} value={quoteForm.name}
                        onChange={(e) => setQuoteForm({ ...quoteForm, name: e.target.value })}
                        data-testid="quote-name-input" required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#0A1628] mb-2">{t('contact.form.email')} *</label>
                      <input
                        type="email" className={inputClass} value={quoteForm.email}
                        onChange={(e) => setQuoteForm({ ...quoteForm, email: e.target.value })}
                        data-testid="quote-email-input" required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#0A1628] mb-2">{t('contact.form.phone')}</label>
                      <input
                        type="tel" className={inputClass} value={quoteForm.phone}
                        onChange={(e) => setQuoteForm({ ...quoteForm, phone: e.target.value })}
                        data-testid="quote-phone-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#0A1628] mb-2">{t('contact.form.company')}</label>
                      <input
                        type="text" className={inputClass} value={quoteForm.company}
                        onChange={(e) => setQuoteForm({ ...quoteForm, company: e.target.value })}
                        data-testid="quote-company-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#0A1628] mb-2">{t('contact.form.products')}</label>
                      <input
                        type="text" className={inputClass} value={quoteForm.products}
                        onChange={(e) => setQuoteForm({ ...quoteForm, products: e.target.value })}
                        data-testid="quote-products-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#0A1628] mb-2">{t('contact.form.quantity')}</label>
                      <input
                        type="text" className={inputClass} value={quoteForm.quantity}
                        onChange={(e) => setQuoteForm({ ...quoteForm, quantity: e.target.value })}
                        data-testid="quote-quantity-input"
                      />
                    </div>
                  </div>
                  <div className="mb-8">
                    <label className="block text-sm font-medium text-[#0A1628] mb-2">{t('contact.form.details')}</label>
                    <textarea
                      className={textareaClass} value={quoteForm.message}
                      onChange={(e) => setQuoteForm({ ...quoteForm, message: e.target.value })}
                      data-testid="quote-message-input"
                    />
                  </div>
                  {/* Layer 3: Honeypot - invisible to humans */}
                  <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
                    <label htmlFor="website_url_q">Website</label>
                    <input
                      type="text" id="website_url_q" name="website_url" tabIndex="-1" autoComplete="off"
                      value={quoteForm.website_url}
                      onChange={(e) => setQuoteForm({ ...quoteForm, website_url: e.target.value })}
                      data-testid="quote-honeypot"
                    />
                  </div>
                  {/* Layer 1: Checkbox "No soy robot" */}
                  <CheckboxCaptcha verified={quoteCheckbox} onVerify={() => setQuoteCheckbox(true)} lang={lang} />
                  {/* Layer 2: Slider */}
                  <SliderCaptcha verified={quoteSlider} onVerify={() => setQuoteSlider(true)} lang={lang} />
                  <button
                    type="submit" disabled={loading}
                    className="w-full bg-[#C5A55A] text-[#0A1628] font-semibold px-8 py-4 uppercase tracking-wider transition-all duration-300 shadow-[0_4px_14px_0_rgba(197,165,90,0.39)] hover:shadow-[0_6px_20px_rgba(197,165,90,0.23)] hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                    data-testid="quote-submit-btn"
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                    {loading ? t('contact.form.sending') : t('contact.form.submitQuote')}
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
